import type { ShopTemplateBlock } from "./types";
import {
    catalogProductSort,
    type CatalogProductSort,
} from "./catalogProductPage";

export const CATEGORY_CATALOG_PRODUCT_RAIL_SOURCE = "bestselling";
export const CATEGORY_CATALOG_PRODUCT_RAIL_DEFAULT_LIMIT = 4;
export const CATEGORY_CATALOG_PRODUCT_RAIL_MAX_LIMIT = 12;

export type CategoryCatalogProductRailConfig = Readonly<{
    limit: number;
    sort: CatalogProductSort;
}>;

function optionText(block: ShopTemplateBlock, key: string): string {
    const value = block.options?.[key];

    return typeof value === "string" ? value.trim() : "";
}

function optionNumber(block: ShopTemplateBlock, key: string, fallback: number): number {
    const value = block.options?.[key];
    const parsed = typeof value === "number" ? value : Number.parseFloat(String(value ?? ""));

    return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Resolves the opt-in catalog query used by an authored product rail on a
 * category or section page. Ordinary home/cart rails deliberately return
 * null and keep using the products already present in their page context.
 */
export function categoryCatalogProductRailConfig(
    block: ShopTemplateBlock | null | undefined,
): CategoryCatalogProductRailConfig | null {
    if (!block || block.type !== "productRail") {
        return null;
    }

    const source = optionText(block, "catalogSource");
    const requestedSort = optionText(block, "catalogSort");

    if (
        source !== CATEGORY_CATALOG_PRODUCT_RAIL_SOURCE
        && requestedSort !== CATEGORY_CATALOG_PRODUCT_RAIL_SOURCE
    ) {
        return null;
    }

    const limit = Math.min(
        CATEGORY_CATALOG_PRODUCT_RAIL_MAX_LIMIT,
        Math.max(1, Math.round(optionNumber(
            block,
            "catalogLimit",
            CATEGORY_CATALOG_PRODUCT_RAIL_DEFAULT_LIMIT,
        ))),
    );

    return {
        limit,
        sort: catalogProductSort(requestedSort || source),
    };
}

export function categoryCatalogProductRailSearchParams({
    currencyCode,
    languageCode,
    limit,
    path,
    sort,
}: CategoryCatalogProductRailConfig & {
    currencyCode?: string | null;
    languageCode?: string | null;
    path: string;
}): URLSearchParams {
    const params = new URLSearchParams({
        limit: String(limit),
        page: "1",
        path,
        sort,
    });

    if (languageCode?.trim()) {
        params.set("language", languageCode.trim());
    }

    if (currencyCode?.trim()) {
        params.set("currency", currencyCode.trim().toUpperCase());
    }

    return params;
}
